import { CaptionedBlockComponent } from '@blocksuite/affine-components/caption';
import {
  CopyIcon,
  DeleteIcon,
} from '@blocksuite/affine-components/icons';
import { toast } from '@blocksuite/affine-components/toast';
import { NOTE_SELECTOR } from '@blocksuite/affine-shared/consts';
import { RANGE_SYNC_EXCLUDE_ATTR } from '@blocksuite/block-std';
import { Rect } from '@blocksuite/global/utils';
import { Slice } from '@blocksuite/store';
import { computed } from '@lit-labs/preact-signals';
import { css, html, nothing, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { prioritizeActionItemsSlot } from '@pique/core/components/blocksuite/block-suite-editor/specs/custom/pique/pique-slots';

import type { NoteBlockComponent } from '../note-block/index.js';
import type { AffineInnerModalWidget } from '../root-block/index.js';
import type { DatabaseBlockModel } from './database-model.js';
import type { DatabaseBlockService } from './database-service.js';

import { DragIndicator, popMenu } from '../_common/components/index.js';
import {
  AffineDragHandleWidget,
  EdgelessRootBlockComponent,
} from '../root-block/index.js';
import {
  captureEventTarget,
  getDropResult,
} from '../root-block/widgets/drag-handle/utils.js';
import { AFFINE_INNER_MODAL_WIDGET } from '../root-block/widgets/inner-modal/inner-modal.js';
import './components/title/index.js';
import { DatabaseBlockDataSource } from './data-source.js';
import { dataViewCommonStyle } from './data-view/common/css-variable.js';
import {
  DataView,
  type DataViewExpose,
  type DataViewProps,
  type DataViewSelection,
  type DataViewWidget,
  type DataViewWidgetProps,
  DatabaseSelection,
  defineUniComponent,
  renderUniLit,
  widgetPresets,
} from './data-view/index.js';
import { DatabaseBlockSchema } from './database-model.js';

@customElement('affine-database')
export class DatabaseBlockComponent extends CaptionedBlockComponent<
  DatabaseBlockModel,
  DatabaseBlockService
> {
  private static readonly PRIORITIZE_BUTTON_INITIAL_DELAY = 2000;
  private static readonly PRIORITIZE_BUTTON_COLLAPSE_DELAY = 3000;

  @state()
  private accessor _prioritizeButtonExpanded = false;

  private _collapseTimeout: number | null = null;
  private _showTimeout: number | null = null;

  private _scheduleCollapse = () => {
    if (this._collapseTimeout) {
      clearTimeout(this._collapseTimeout);
    }
    this._collapseTimeout = window.setTimeout(() => {
      this._prioritizeButtonExpanded = false;
      this._collapseTimeout = null;
    }, DatabaseBlockComponent.PRIORITIZE_BUTTON_COLLAPSE_DELAY);
  };

  private _onPrioritizeHover = (isEntering: boolean) => {
    if (isEntering) {
      if (this._collapseTimeout) {
        clearTimeout(this._collapseTimeout);
      }
      this._prioritizeButtonExpanded = true;
    } else {
      this._scheduleCollapse();
    }
  };

  _bindHotkey: DataViewProps['bindHotkey'] = hotkeys => {
    return {
      dispose: this.host.event.bindHotkey(hotkeys, {
        blockId: this.topContenteditableElement?.blockId ?? this.blockId,
      }),
    };
  };

  // @ts-expect-error - kept for future use but currently unused
  private _clickDatabaseOps = (e: MouseEvent) => {
    popMenu(e.currentTarget as HTMLElement, {
      options: {
        input: {
          initValue: this.model.title.toString(),
          placeholder: 'Untitled',
          onComplete: text => {
            this.model.title.replace(0, this.model.title.length, text);
          },
        },
        items: [
          {
            type: 'action',
            icon: CopyIcon,
            name: 'Copy',
            select: () => {
              const slice = Slice.fromModels(this.doc, [this.model]);
              this.std.clipboard
                .copySlice(slice)
                .then(() => {
                  toast(this.host, 'Copied to clipboard');
                })
                .catch(console.error);
            },
          },
          // {
          //   type: 'action',
          //   icon: DuplicateIcon,
          //   name: 'Duplicate',
          //   select: () => {
          //   },
          // },
          {
            type: 'group',
            name: '',
            children: () => [
              {
                type: 'action',
                icon: DeleteIcon,
                class: 'delete-item',
                name: 'Delete Database',
                select: () => {
                  this.model.children.slice().forEach(block => {
                    this.doc.deleteBlock(block);
                  });
                  this.doc.deleteBlock(this.model);
                },
              },
            ],
          },
        ],
      },
    });
  };

  private _dataSource?: DatabaseBlockDataSource;

  _handleEvent: DataViewProps['handleEvent'] = (name, handler) => {
    return {
      dispose: this.host.event.add(name, handler, {
        blockId: this.blockId,
      }),
    };
  };

  private dataView = new DataView();

  private renderTitle = (dataViewMethod: DataViewExpose) => {
    const addRow = () => dataViewMethod.addRow?.('start');
    return html` <affine-database-title
      style="overflow: hidden"
      .titleText="${this.model.title}"
      .readonly="${this.doc.readonly}"
      .onPressEnterKey="${addRow}"
    ></affine-database-title>`;
  };

  static override styles = css`
    ${unsafeCSS(dataViewCommonStyle('affine-database'))}
    affine-database {
      display: block;
      border-radius: 8px;
      background-color: var(--affine-background-primary-color);
      padding: 8px;
      margin: 8px -8px -8px;
    }

    .database-block-selected {
      background-color: var(--affine-hover-color);
      border-radius: 4px;
    }

    .database-ops {
      margin-top: 4px;
      padding: 2px;
      border-radius: 4px;
      display: flex;
      cursor: pointer;
    }

    .database-ops svg {
      width: 16px;
      height: 16px;
      color: var(--affine-icon-color);
    }

    .database-ops:hover {
      background-color: var(--affine-hover-color);
    }

    .prioritize-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--pique-green-gradient-light);
      border: 1px solid var(--pique-green-border);
      color: var(--affine-text-primary-color);
      font-size: 12px;
      font-weight: 500;
      font-family: Outfit, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      cursor: pointer;
      white-space: nowrap;
      overflow: hidden;
      box-shadow: var(--pique-green-shadow-medium);
      flex-shrink: 0;
    }

    .prioritize-button.expanded {
      gap: 6px;
      padding: 5px 11px;
      border-radius: 16px;
      animation: expandBanner 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }

    .prioritize-button.collapsed {
      gap: 0;
      padding: 0;
      border-radius: 50%;
      width: 28px;
      height: 28px;
      transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
      animation: collapseBanner 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }

    .prioritize-button:hover {
      background: var(--pique-green-gradient-hover);
      border-color: var(--pique-green-border-hover);
      box-shadow: var(--pique-green-shadow-strong);
    }

    .prioritize-button.expanded:hover {
      transform: translateY(-1px);
    }

    .prioritize-button.collapsed:hover {
      width: auto;
      border-radius: 16px;
      padding: 5px 11px;
      gap: 6px;
      transform: translateY(-1px);
      transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .prioritize-button.collapsed:hover .prioritize-text {
      opacity: 1;
      max-width: 120px;
      transition: opacity 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), max-width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .prioritize-button:active {
      transform: translateY(0);
    }

    .prioritize-icon {
      width: 18px;
      height: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .prioritize-icon svg {
      width: 18px;
      height: 18px;
    }

    .prioritize-text {
      opacity: 1;
      max-width: 120px;
      transition: opacity 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), max-width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .prioritize-button.collapsed .prioritize-text {
      opacity: 0;
      max-width: 0;
      overflow: hidden;
      transition: opacity 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), max-width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    @keyframes expandBanner {
      0% {
        width: 28px;
        border-radius: 50%;
        padding: 0;
        gap: 0;
        box-shadow: var(--pique-green-shadow-subtle);
      }
      50% {
        box-shadow: var(--pique-green-shadow-strong);
      }
      100% {
        width: auto;
        border-radius: 16px;
        padding: 5px 11px;
        gap: 6px;
        box-shadow: var(--pique-green-shadow-medium);
      }
    }

    @keyframes collapseBanner {
      0% {
        width: auto;
        border-radius: 16px;
        padding: 5px 11px;
        gap: 6px;
        box-shadow: var(--pique-green-shadow-medium);
      }
      100% {
        width: 28px;
        border-radius: 50%;
        padding: 0;
        gap: 0;
        box-shadow: var(--pique-green-shadow-subtle);
      }
    }

    @media print {
      .database-ops {
        display: none;
      }

      .prioritize-button {
        display: none;
      }

      .database-header-bar {
        display: none !important;
      }
    }
  `;

  getRootService = () => {
    return this.std.spec.getService('affine:page');
  };

  headerWidget: DataViewWidget = defineUniComponent(
    (props: DataViewWidgetProps) => {
      return html`
        <div style="margin-bottom: 16px;display:flex;flex-direction: column">
          <div style="display:flex;gap:8px;padding: 0 6px;margin-bottom: 8px;align-items:center;">
            ${this.renderTitle(props.viewMethods)} ${this.renderDatabaseOps()} ${this.renderPrioritizeButton()}
          </div>
          <div
            style="display:flex;align-items:center;justify-content: space-between;gap: 12px"
            class="database-header-bar"
          >
            <div style="flex:1">
              ${renderUniLit(widgetPresets.viewBar, props)}
            </div>
            ${renderUniLit(this.toolsWidget, props)}
          </div>
          ${renderUniLit(widgetPresets.filterBar, props)}
        </div>
      `;
    }
  );

  indicator = new DragIndicator();

  onDrag = (evt: MouseEvent, id: string): (() => void) => {
    const result = getDropResult(evt);
    if (result && result.rect) {
      document.body.append(this.indicator);
      this.indicator.rect = Rect.fromLWTH(
        result.rect.left,
        result.rect.width,
        result.rect.top,
        result.rect.height
      );
      return () => {
        this.indicator.remove();
        const model = this.doc.getBlock(id)?.model;
        const target = this.doc.getBlock(result.dropBlockId)?.model ?? null;
        let parent = this.doc.getParent(result.dropBlockId);
        const shouldInsertIn = result.dropType === 'in';
        if (shouldInsertIn) {
          parent = target;
        }
        if (model && target && parent) {
          if (shouldInsertIn) {
            this.doc.moveBlocks([model], parent);
          } else {
            this.doc.moveBlocks(
              [model],
              parent,
              target,
              result.dropType === 'before'
            );
          }
        }
      };
    }
    this.indicator.remove();
    return () => {};
  };

  setSelection = (selection: DataViewSelection | undefined) => {
    this.selection.setGroup(
      'note',
      selection
        ? [
            new DatabaseSelection({
              blockId: this.blockId,
              viewSelection: selection,
            }),
          ]
        : []
    );
  };

  toolsWidget: DataViewWidget = widgetPresets.createTools({
    table: [
      widgetPresets.tools.filter,
      widgetPresets.tools.expand,
      widgetPresets.tools.search,
      widgetPresets.tools.viewOptions,
      widgetPresets.tools.tableAddRow,
    ],
    kanban: [
      widgetPresets.tools.filter,
      widgetPresets.tools.expand,
      widgetPresets.tools.search,
      widgetPresets.tools.viewOptions,
    ],
  });

  viewSelection$ = computed(() => {
    const databaseSelection = this.selection.value.find(
      (selection): selection is DatabaseSelection => {
        if (selection.blockId !== this.blockId) {
          return false;
        }
        return selection instanceof DatabaseSelection;
      }
    );
    return databaseSelection?.viewSelection;
  });

  private renderDatabaseOps() {
    return nothing;
    // if (this.doc.readonly) {
    //   return nothing;
    // }
    // return html` <div class="database-ops" @click="${this._clickDatabaseOps}">
    //   ${MoreHorizontalIcon}
    // </div>`;
  }

  private renderPrioritizeButton() {
    const currentTitle = this.model.title?.toString() || '';
    if (currentTitle !== 'Action Items' || this.doc.readonly) {
      return nothing;
    }

    const buttonClasses = classMap({
      'prioritize-button': true,
      'expanded': this._prioritizeButtonExpanded,
      'collapsed': !this._prioritizeButtonExpanded,
    });

    return html`<button
      class="${buttonClasses}"
      @click=${() => prioritizeActionItemsSlot.emit()}
      @mouseenter=${() => this._onPrioritizeHover(true)}
      @mouseleave=${() => this._onPrioritizeHover(false)}
      title="Prioritize action items"
    >
      <span class="prioritize-icon">
        <svg width="18" height="18" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.7525 8.2971L17.0284 10.8699C17.2024 11.2281 17.6664 11.5716 18.0577 11.6373L20.3703 12.0248C21.8492 12.2733 22.197 13.355 21.1314 14.4221L19.3336 16.2348C19.0292 16.5418 18.8625 17.1338 18.9567 17.5577L19.4713 19.8016C19.8773 21.5778 18.9422 22.2648 17.3836 21.3365L15.2162 20.0428C14.8247 19.8089 14.1794 19.8089 13.7808 20.0428L11.6132 21.3365C10.0619 22.2648 9.11952 21.5704 9.52547 19.8016L10.0402 17.5577C10.1344 17.1338 9.96768 16.5418 9.66321 16.2348L7.86541 14.4221C6.80703 13.355 7.14773 12.2733 8.62657 12.0248L10.9391 11.6373C11.3233 11.5716 11.7872 11.2281 11.9612 10.8699L13.2371 8.2971C13.9331 6.90105 15.0639 6.90105 15.7525 8.2971Z"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            fill="none"
          />
        </svg>
      </span>
      <span class="prioritize-text">Prioritize Me!</span>
    </button>`;
  }

  override connectedCallback() {
    super.connectedCallback();

    const currentTitle = this.model.title?.toString() || '';
    if (currentTitle === 'Action Items') {
      this._showTimeout = window.setTimeout(() => {
        this._prioritizeButtonExpanded = true;
        this._scheduleCollapse();
        this._showTimeout = null;
      }, DatabaseBlockComponent.PRIORITIZE_BUTTON_INITIAL_DELAY);
    }

    this.setAttribute(RANGE_SYNC_EXCLUDE_ATTR, 'true');
    let canDrop = false;
    this.disposables.add(
      AffineDragHandleWidget.registerOption({
        flavour: DatabaseBlockSchema.model.flavour,
        onDragMove: state => {
          const target = captureEventTarget(state.raw.target);
          const view = this.view;
          if (view && target instanceof HTMLElement && this.contains(target)) {
            canDrop = view.showIndicator?.(state.raw) ?? false;
            return false;
          }
          if (canDrop) {
            view?.hideIndicator?.();
            canDrop = false;
          }
          return false;
        },
        onDragEnd: ({ state, draggingElements }) => {
          const target = state.raw.target;
          const view = this.view;
          if (
            canDrop &&
            view &&
            view.moveTo &&
            target instanceof HTMLElement &&
            this.parentElement?.contains(target)
          ) {
            const blocks = draggingElements.map(v => v.model);
            this.doc.moveBlocks(blocks, this.model);
            blocks.forEach(model => {
              view.moveTo?.(model.id, state.raw);
            });
            view.hideIndicator?.();
            return false;
          }
          if (canDrop) {
            view?.hideIndicator?.();
            canDrop = false;
          }
          return false;
        },
      })
    );
  }

  override renderBlock() {
    const peekViewService = this.getRootService().peekViewService;
    return html`
      <div
        contenteditable="false"
        style="position: relative;background-color: var(--affine-background-primary-color);border-radius: 4px"
      >
        ${this.dataView.render({
          bindHotkey: this._bindHotkey,
          handleEvent: this._handleEvent,
          selection$: this.viewSelection$,
          setSelection: this.setSelection,
          dataSource: this.dataSource,
          headerWidget: this.headerWidget,
          onDrag: this.onDrag,
          std: this.std,
          detailPanelConfig: {
            openDetailPanel: peekViewService
              ? (target, template) => peekViewService.peek(target, template)
              : undefined,
            target: () => this.innerModalWidget.target,
          },
        })}
      </div>
    `;
  }

  get dataSource(): DatabaseBlockDataSource {
    if (!this._dataSource) {
      this._dataSource = new DatabaseBlockDataSource(this.host, {
        pageId: this.host.doc.id,
        blockId: this.model.id,
      });
    }
    return this._dataSource;
  }

  get innerModalWidget() {
    return this.rootComponent!.widgetComponents[
      AFFINE_INNER_MODAL_WIDGET
    ] as AffineInnerModalWidget;
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this._collapseTimeout) {
      clearTimeout(this._collapseTimeout);
      this._collapseTimeout = null;
    }
    if (this._showTimeout) {
      clearTimeout(this._showTimeout);
      this._showTimeout = null;
    }
  }

  override get topContenteditableElement() {
    if (this.rootComponent instanceof EdgelessRootBlockComponent) {
      const note = this.closest<NoteBlockComponent>(NOTE_SELECTOR);
      return note;
    }
    return this.rootComponent;
  }

  get view() {
    return this.dataView.expose;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    // @ts-expect-error vendored blocksuite
    'affine-database': DatabaseBlockComponent;
  }
}
